import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import {
  DEFAULT_EXEC_COMMAND,
  statCpuPercent,
  statMemPercent,
  type ClientInstance,
  type ClientInstanceStatus,
  type InstanceActionType,
  type InstanceStatsData,
} from "../infrastructure.types";
import { canManageInfra, isInfraAdmin } from "../rbac";
import { runInstanceAction } from "../instanceActions";
import { followHandOff } from "../InstanceActions/InstanceActionsStore";
import {
  isSocketConnected,
  observeConnection,
  subscribeLogs,
  subscribeStats,
  startExecSession,
  subscribeInstanceErrors,
  type StreamHandle,
  type ExecSessionHandle,
} from "../instanceStreams";
import type { InstanceStreamError } from "../infrastructure.types";
import { isSessionEnded, sessionMark } from "stores/sessionGuard";

/** How many stats samples to retain for the sparklines. */
const STATS_HISTORY = 60;

/**
 * View store for the InstanceDetail console (route /infrastructure/instances/:id).
 *
 * Owns the lifecycle of THREE staff-only live streams for one container — logs,
 * stats, exec — plus the instance record and its action/deploy history. Plain
 * class with `makeAutoObservable` (route ViewStores never extend ViewStore).
 * The instance record is read via the registered `client-instances` ServiceStore
 * (`get`) and mutating actions go through the `instance-actions` ServiceStore.
 *
 * LIFECYCLE / LEAK SAFETY (the critical part):
 *  - The route `onEnter` calls `initialize(id)`; `onExit`/`onParamsChange` and
 *    the view's unmount call `dispose()`. `dispose()` stops EVERY stream handle
 *    and the exec session. The stream helpers ALSO tear down on socket
 *    `disconnect`, so a dropped connection can't leak a half-open stream.
 *  - Log text is NOT buffered in the store (it goes straight to xterm via the
 *    `onLogChunk` sink the view registers) so we don't grow unbounded MobX
 *    state; only a bounded stats ring buffer is observable.
 *  - Exec output likewise flows to a view-registered sink; the store only tracks
 *    session on/off + error.
 *
 * SECURITY: streams are staff-only and never sent to portal channels; exec is
 * admin-only (gated here AND by the API). No secret is read or surfaced.
 *
 * UNTESTABLE: no Portainer/Docker/API-Phase-5 in this env — see instanceStreams.
 */
class InstanceDetailStore {
  rootStore: RootStore;

  instanceId: string | null = null;
  instance: ClientInstance | null = null;
  loading = false;
  error: string | null = null;

  // ---- Action state (start/stop/restart/deploy/rollback from this view) ----
  actioning = false;
  actionError: string | null = null;
  /** In-flight enable/disable toggle (operator+). */
  togglingEnabled = false;

  // ---- Log stream ----
  logsSubscribed = false;
  logsError: string | null = null;
  /** Sink the view registers so chunks land in xterm, not in MobX state. */
  private logSink: ((chunk: string) => void) | null = null;
  private logsHandle: StreamHandle | null = null;

  // ---- Stats stream ----
  statsSubscribed = false;
  statsError: string | null = null;
  /** Bounded ring buffer of recent samples for the sparklines. */
  statsHistory: InstanceStatsData[] = [];
  latestStats: InstanceStatsData | null = null;
  private statsHandle: StreamHandle | null = null;

  // ---- Shared error channel (instance:error) ----
  private errorHandle: (() => void) | null = null;

  // ---- Socket connect/disconnect observer (keeps the indicator honest and
  //      re-establishes the streams after an auto-reconnect) ----
  private connHandle: (() => void) | null = null;

  // ---- Exec session (admin only) ----
  execActive = false;
  execStarting = false;
  execError: string | null = null;
  /** Sink the view registers so exec output lands in the exec xterm. */
  private execSink: ((chunk: string) => void) | null = null;
  private execHandle: ExecSessionHandle | null = null;
  /** Last xterm geometry reported by the view; replayed when the PTY opens so
   *  the server-side resize lands even when the fit ran before `ready`. */
  private lastExecSize: { cols: number; rows: number } | null = null;

  // ---- Connection ----
  socketConnected = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    // NOTE: the private stream handles + view-registered sinks are imperative
    // plumbing. MobX's AnnotationsMap can't reference private fields, so they
    // stay auto-observable; every assignment to them is therefore wrapped in
    // `runInAction` (see start*/stop*/set*Sink) so no mutation happens outside
    // an action. They are never read reactively — only the *Subscribed/active
    // flags drive the UI.
    makeAutoObservable(this, { rootStore: false });
  }

  /** The registered `client-instances` ServiceStore (single-record `get`). */
  private get instancesService(): ServiceStoreType | null {
    return (
      ((this.rootStore.serviceStoreManager?.getStore("client-instances") ?? this.rootStore.clientInstancesStore) as unknown as ServiceStoreType | null) ?? null
    );
  }

  /** The registered `instance-actions` ServiceStore (the mutating action seam). */
  private get actionsService(): ServiceStoreType | null {
    return (
      ((this.rootStore.serviceStoreManager?.getStore("instance-actions") ?? this.rootStore.instanceActionsStore) as unknown as ServiceStoreType | null) ?? null
    );
  }

  // ---- RBAC ----

  get canManage(): boolean {
    return canManageInfra(this.rootStore);
  }

  /** Exec terminal is admin-only. */
  get canExec(): boolean {
    return isInfraAdmin(this.rootStore);
  }

  // ---- Entry / teardown ----

  /** Route entry. Loads the record and starts the log + stats streams. */
  async initialize(instanceId: string) {
    // If re-entering for the same id, refresh but don't double-subscribe.
    const changed = this.instanceId !== instanceId;
    if (changed) this.dispose();
    this.instanceId = instanceId;
    runInAction(() => {
      this.socketConnected = isSocketConnected(this.rootStore);
    });
    // loadInstance swallows a sign-out: no observer and no stream may open in
    // the next person's session for the record the previous one was viewing.
    const ended = sessionMark(this.rootStore);
    await this.loadInstance();
    if (ended()) return;
    // Watch socket connect/disconnect so the indicator stays honest and the
    // streams re-establish after an auto-reconnect.
    void this.startConnectionObserver();
    // NEVER open live streams for a record that failed to load — the body shows
    // the error and burning stream slots for an invisible console is wrong.
    if (!this.instance) return;
    // Subscribe to the shared error channel, then begin the live streams
    // (idempotent — guarded by the *Subscribed flags).
    void this.startErrorChannel();
    void this.startLogs();
    void this.startStats();
  }

  /**
   * Tear down EVERYTHING — every stream handle, the exec session, and the error
   * channel. Idempotent. Called from the route onExit/onParamsChange and the
   * view's unmount effect.
   */
  dispose() {
    this.stopLogs();
    this.stopStats();
    this.stopExec();
    this.errorHandle?.();
    this.errorHandle = null;
    this.connHandle?.();
    this.connHandle = null;
    runInAction(() => {
      this.statsHistory = [];
      this.latestStats = null;
      this.logSink = null;
      this.execSink = null;
      this.logsError = null;
      this.statsError = null;
    });
  }

  /** Subscribe to the API's `instance:error` channel and route by stream kind. */
  private async startErrorChannel() {
    if (this.errorHandle) return;
    this.errorHandle = await subscribeInstanceErrors(
      this.rootStore,
      (err) => this.handleStreamError(err)
    );
  }

  /**
   * Watch the shared socket's connect/disconnect transitions:
   *  - on `disconnect`: the per-stream teardown has already stopped the handles
   *    server-side; reflect that in the UI (drop the stale "streaming" flags and
   *    null the dead handles) so a reconnect can cleanly re-subscribe.
   *  - on `connect` (auto-reconnect): re-establish the log + stats streams. The
   *    `start*` are guarded against double-subscribe, so this can't stack
   *    duplicate streams. Exec is intentionally NOT auto-restarted — an admin
   *    shell must be explicitly reopened.
   */
  private async startConnectionObserver() {
    if (this.connHandle) return;
    // observeConnection returns null when no socket object exists yet; leave
    // connHandle null so the next stream attempt retries the binding.
    const handle = await observeConnection(
      this.rootStore,
      () => this.handleSocketConnect(),
      () => this.handleSocketDisconnect()
    );
    if (handle) {
      runInAction(() => {
        this.connHandle = handle;
      });
    }
  }

  private handleSocketDisconnect() {
    // The stream handles were torn down by their own disconnect wiring; drop our
    // references and flags so the panels show "idle"/"disconnected" truthfully.
    runInAction(() => {
      this.logsHandle = null;
      this.statsHandle = null;
      this.execHandle = null;
      this.socketConnected = false;
      this.logsSubscribed = false;
      this.statsSubscribed = false;
      this.execActive = false;
      this.execStarting = false;
    });
  }

  private handleSocketConnect() {
    runInAction(() => {
      this.socketConnected = true;
    });
    // Re-establish the live log + stats streams (idempotent via handle-null
    // guards). Exec is not auto-resumed.
    void this.startLogs();
    void this.startStats();
  }

  private handleStreamError(err: InstanceStreamError) {
    runInAction(() => {
      if (err.kind === "stats") this.statsError = err.message;
      else if (err.kind === "exec") {
        this.execError = err.message;
        // A denied/failed exec start never becomes active.
        this.execActive = false;
        this.execStarting = false;
      } else this.logsError = err.message;
    });
  }

  // ---- Instance record ----

  async loadInstance() {
    if (!this.instanceId) return;
    this.loading = true;
    this.error = null;
    try {
      const instance = (await this.instancesService?.get(
        this.instanceId
      )) as unknown as ClientInstance | null;
      runInAction(() => {
        this.instance = instance ?? null;
      });
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) {
          this.error =
            err instanceof Error ? err.message : "Failed to load instance";
        }
      });
      console.error("Failed to load instance:", err);
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async reload() {
    const ended = sessionMark(this.rootStore);
    await this.loadInstance();
    if (ended()) return;
    // If a previously failed record now loads (e.g. transient API outage), the
    // streams were never started in initialize — start them now (idempotent).
    if (this.instance) {
      void this.startErrorChannel();
      void this.startLogs();
      void this.startStats();
    }
  }

  // ---- Actions (from the detail view) -------------------------------------
  // These mirror the grid's action transport but operate on the single loaded
  // record, optimistically flipping its status and re-pulling for the truth.
  // operator+ for lifecycle/deploy/rollback (the API re-enforces). Confirmation
  // for stop/restart is handled by the shared InstanceActionsStore dialog before
  // these run.

  private optimisticStatusFor(
    action: InstanceActionType
  ): ClientInstanceStatus | null {
    switch (action) {
      case "start":
        return "starting";
      case "restart":
        return "restarting";
      case "stop":
        return "stopped";
      default:
        return null;
    }
  }

  /** Start / Stop / Restart the loaded instance (operator+). */
  async runLifecycleFromDetail(
    instance: ClientInstance,
    action: InstanceActionType,
    /** The confirm dialog's progress token (InstanceActionsStore.runConfirmed). */
    progressToken?: string
  ): Promise<boolean> {
    if (!this.canManage) return false;
    this.actioning = true;
    this.actionError = null;
    const optimistic = this.optimisticStatusFor(action);
    if (optimistic && this.instance && this.instance.id === instance.id) {
      runInAction(() => {
        this.instance = { ...this.instance!, status: optimistic };
      });
    }
    try {
      const result = await runInstanceAction(this.actionsService, {
        action,
        instanceId: instance.id,
        ...(progressToken ? { progressToken } : {}),
      });
      followHandOff(this.rootStore, result, instance);
      await this.loadInstance();
      return true;
    } catch (err: unknown) {
      // A sign-out ended the call: no error, and no follow-up read, which
      // would go out in the next person's session.
      if (isSessionEnded(err)) return false;
      runInAction(() => {
        this.actionError =
          err instanceof Error ? err.message : `Failed to ${action} instance`;
      });
      await this.loadInstance();
      console.error(`Instance ${action} failed:`, err);
      return false;
    } finally {
      runInAction(() => {
        this.actioning = false;
      });
    }
  }

  /**
   * Toggle the operator action gate (client_instances.enabled) for the loaded
   * instance (operator+). Routed through `instance-actions` as the audited
   * 'enable' / 'disable' verbs; the record is re-pulled for the truth.
   */
  async setEnabled(enabled: boolean): Promise<boolean> {
    if (!this.instance || !this.canManage || this.togglingEnabled) return false;
    const instanceId = this.instance.id;
    this.togglingEnabled = true;
    this.actionError = null;
    runInAction(() => {
      if (this.instance && this.instance.id === instanceId) {
        this.instance = { ...this.instance, enabled };
      }
    });
    try {
      await runInstanceAction(this.actionsService, {
        action: enabled ? "enable" : "disable",
        instanceId,
      });
      await this.loadInstance();
      return true;
    } catch (err: unknown) {
      // A sign-out ended the call: no error, and no follow-up read, which
      // would go out in the next person's session.
      if (isSessionEnded(err)) return false;
      runInAction(() => {
        this.actionError =
          err instanceof Error
            ? err.message
            : `Failed to ${enabled ? "enable" : "disable"} instance`;
      });
      await this.loadInstance();
      console.error("Instance enable/disable failed:", err);
      return false;
    } finally {
      runInAction(() => {
        this.togglingEnabled = false;
      });
    }
  }

  // ---- Log stream ----

  /** The view registers its xterm write fn here so chunks bypass MobX state. */
  setLogSink(sink: ((chunk: string) => void) | null) {
    this.logSink = sink;
  }

  async startLogs() {
    if (!this.instanceId || this.logsHandle) return;
    // Never stream for a record that failed to load.
    if (!this.instance) return;
    runInAction(() => {
      this.logsError = null;
    });
    const handle = await subscribeLogs(
      this.rootStore,
      this.instanceId,
      (data) => {
        // Straight to the terminal; never accumulate in observable state. The
        // API emits the log text under `line`; append a newline since the API
        // splits on lines and xterm (convertEol) handles CR/LF.
        this.logSink?.(`${data.line ?? ""}\r\n`);
      }
    );
    if (!handle) {
      // No live socket — surface the truth instead of a fake 'streaming' state
      // backed by a no-op handle. Re-arm the connection observer so a later
      // connect re-subscribes.
      runInAction(() => {
        this.logsSubscribed = false;
        this.socketConnected = false;
        this.logsError = "Socket disconnected — live logs are unavailable";
      });
      void this.startConnectionObserver();
      return;
    }
    runInAction(() => {
      this.logsHandle = handle;
      this.logsSubscribed = true;
      this.logsError = null;
      this.socketConnected = isSocketConnected(this.rootStore);
    });
  }

  stopLogs() {
    this.logsHandle?.stop();
    this.logsHandle = null;
    runInAction(() => {
      this.logsSubscribed = false;
    });
  }

  // ---- Stats stream ----

  async startStats() {
    if (!this.instanceId || this.statsHandle) return;
    // Never stream for a record that failed to load.
    if (!this.instance) return;
    runInAction(() => {
      this.statsError = null;
    });
    const handle = await subscribeStats(
      this.rootStore,
      this.instanceId,
      (data) => this.pushStats(data)
    );
    if (!handle) {
      runInAction(() => {
        this.statsSubscribed = false;
        this.socketConnected = false;
        this.statsError = "Socket disconnected — live stats are unavailable";
      });
      void this.startConnectionObserver();
      return;
    }
    runInAction(() => {
      this.statsHandle = handle;
      this.statsSubscribed = true;
      this.statsError = null;
      this.socketConnected = isSocketConnected(this.rootStore);
    });
  }

  private pushStats(sample: InstanceStatsData) {
    runInAction(() => {
      this.latestStats = sample;
      const next = [...this.statsHistory, sample];
      // Bounded ring buffer.
      this.statsHistory =
        next.length > STATS_HISTORY ? next.slice(next.length - STATS_HISTORY) : next;
    });
  }

  stopStats() {
    this.statsHandle?.stop();
    this.statsHandle = null;
    runInAction(() => {
      this.statsSubscribed = false;
    });
  }

  /** Recent CPU % series for the sparkline. */
  get cpuSeries(): number[] {
    return this.statsHistory.map((s) => statCpuPercent(s));
  }

  /** Recent memory % series for the sparkline. */
  get memSeries(): number[] {
    return this.statsHistory.map((s) => statMemPercent(s));
  }

  // ---- Exec session (admin only) ----

  /** The view registers its exec-xterm write fn here. */
  setExecSink(sink: ((chunk: string) => void) | null) {
    this.execSink = sink;
  }

  /**
   * Start an admin exec session. Gated on {@link canExec}; the API re-enforces.
   * The command is the default shell argv ARRAY — never a shell string. The
   * session is marked active only once the API signals the PTY is ready
   * (`onReady`); output flows to the registered exec sink.
   */
  async startExec() {
    if (!this.instanceId || !this.canExec || this.execHandle) return;
    this.execStarting = true;
    this.execError = null;
    try {
      const handle = await startExecSession(
        this.rootStore,
        {
          instanceId: this.instanceId,
          cmd: [...DEFAULT_EXEC_COMMAND],
        },
        (chunk) => this.execSink?.(chunk),
        () =>
          runInAction(() => {
            // Session closed server-side (process exit or teardown).
            this.execActive = false;
            this.execHandle = null;
          }),
        () => {
          runInAction(() => {
            // PTY is open and accepting input.
            this.execActive = true;
            this.execStarting = false;
          });
          // Replay the latest xterm geometry now the PTY exists server-side —
          // the exec is created at the Docker default (80x24) otherwise.
          if (this.lastExecSize) {
            this.execHandle?.resize(
              this.lastExecSize.cols,
              this.lastExecSize.rows
            );
          }
        }
      );
      if (!handle) {
        // No live socket — never pretend the shell is open.
        runInAction(() => {
          this.execActive = false;
          this.execError =
            "Socket disconnected — cannot start an exec session";
        });
        void this.startConnectionObserver();
        return;
      }
      // Optimistically mark active even before `ready` so the terminal accepts
      // typing immediately; `onReady` confirms and `onEnd` clears it.
      runInAction(() => {
        this.execHandle = handle;
        this.execActive = true;
      });
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) {
          this.execError =
            err instanceof Error ? err.message : "Failed to start exec session";
        }
      });
      console.error("Exec start failed:", err);
    } finally {
      runInAction(() => {
        this.execStarting = false;
      });
    }
  }

  /** Push a keystroke / paste to the exec session. */
  sendExecInput(input: string) {
    this.execHandle?.send(input);
  }

  /**
   * Report the xterm geometry (called by the view on every fit/resize). Always
   * remembered so it can be replayed when a session opens; relayed immediately
   * when a session is live so full-screen tools render correctly.
   */
  sendExecResize(cols: number, rows: number) {
    if (!Number.isFinite(cols) || !Number.isFinite(rows) || cols < 1 || rows < 1) {
      return;
    }
    this.lastExecSize = { cols, rows };
    this.execHandle?.resize(cols, rows);
  }

  stopExec() {
    this.execHandle?.stop();
    this.execHandle = null;
    runInAction(() => {
      this.execActive = false;
      this.execStarting = false;
    });
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out
   * is left for the next one. Called by RootStore.purgeUserData on sign-out.
   */
  reset() {
    this.dispose();
    this.instanceId = null;
    this.instance = null;
    this.loading = false;
    this.error = null;
    this.actioning = false;
    this.actionError = null;
    this.togglingEnabled = false;
    this.logsSubscribed = false;
    this.logsError = null;
    this.logSink = null;
    this.logsHandle = null;
    this.statsSubscribed = false;
    this.statsError = null;
    this.statsHistory = [];
    this.latestStats = null;
    this.statsHandle = null;
    this.errorHandle = null;
    this.connHandle = null;
    this.execActive = false;
    this.execStarting = false;
    this.execError = null;
    this.execSink = null;
    this.execHandle = null;
    this.lastExecSize = null;
    this.socketConnected = false;
  }
}

export default InstanceDetailStore;
